/**
 * Service for processing OCR text with Google Gemini AI
 * Extracts structured business card information from raw OCR text
 */

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3-pro-preview:generateContent';

/**
 * Analyzes OCR text with Gemini to extract business card information
 * @param {string} ocrText - Raw text extracted from OCR
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<Object>} Extracted and structured data
 */
export const analyzeBusinessCardWithGemini = async (ocrText, language = 'fr') => {
    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not found in environment variables');
        throw new Error('Gemini API key is not configured');
    }

    const prompt = language === 'fr'
        ? `Analyse ce texte extrait d'une carte de visite par OCR et extrait les informations suivantes au format JSON strict.

IMPORTANT: Réponds UNIQUEMENT avec un objet JSON valide, sans aucun texte avant ou après.

Texte OCR:
"""
${ocrText}
"""

Extrait ces champs (laisse vide "" si non trouvé):
- firstName: Prénom
- lastName: Nom de famille
- email: Adresse email
- phone: Numéro de téléphone (format international si possible)
- company: Nom de l'entreprise
- position: Fonction/poste
- address: Adresse complète
- website: Site web (avec https:// si trouvé)

Réponds UNIQUEMENT avec ce format JSON:
{
  "firstName": "",
  "lastName": "",
  "email": "",
  "phone": "",
  "company": "",
  "position": "",
  "address": "",
  "website": ""
}`
        : `Analyze this text extracted from a business card via OCR and extract the following information in strict JSON format.

IMPORTANT: Respond ONLY with a valid JSON object, no text before or after.

OCR Text:
"""
${ocrText}
"""

Extract these fields (leave empty "" if not found):
- firstName: First name
- lastName: Last name
- email: Email address
- phone: Phone number (international format if possible)
- company: Company name
- position: Job title/position
- address: Full address
- website: Website (with https:// if found)

Respond ONLY with this JSON format:
{
  "firstName": "",
  "lastName": "",
  "email": "",
  "phone": "",
  "company": "",
  "position": "",
  "address": "",
  "website": ""
}`;

    try {
        console.log('Sending request to Gemini API...');

        const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }],
                generationConfig: {
                    temperature: 0.1,
                    topK: 1,
                    topP: 1,
                    maxOutputTokens: 2048,
                },
                systemInstruction: {
                    parts: [{
                        text: "You are a data extraction assistant. Always respond with valid JSON only, no explanations."
                    }]
                }
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();
        //console.log('Gemini API full response:', JSON.stringify(data, null, 2));

        // Check different possible response structures
        let generatedText = '';

        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                generatedText = data.candidates[0].content.parts[0].text;
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            } else if (data.candidates[0].output) {
                generatedText = data.candidates[0].output;
            }
        } else if (data.text) {
            generatedText = data.text;
        } else if (data.output) {
            generatedText = data.output;
        }

        if (!generatedText) {
            console.error('Could not find text in response structure:', data);
            throw new Error('Invalid response format from Gemini API - no text found');
        }

        console.log('Generated text:', generatedText);

        // Extract JSON from the response (handle cases where there might be extra text)
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            throw new Error('No JSON found in Gemini response');
        }

        const extractedData = JSON.parse(jsonMatch[0]);
        console.log('Extracted data from Gemini:', extractedData);

        // Filter out empty values
        const filteredData = {};
        for (const [key, value] of Object.entries(extractedData)) {
            if (value && value.trim() !== '') {
                filteredData[key] = value.trim();
            }
        }

        return filteredData;

    } catch (error) {
        console.error('Error analyzing with Gemini:', error);
        throw error;
    }
};

/**
 * Check if Gemini API is available and configured
 * @returns {boolean} True if Gemini is available
 */
export const isGeminiAvailable = () => {
    return !!GEMINI_API_KEY;
};
